/**
 * Refine — pure geometry helpers + file-reading utility.
 * Extracted from RefineDialog so the parent component stays UI-focused.
 */

export const MAX_AREAS = 3;
export const MAX_REF_KB = 3000; // 3 MB cap per area reference image
export const ALLOWED_REF_TYPES = ["image/png", "image/jpeg", "image/webp"];
export const REF_KIND_LABEL = {
  gemstone: "Gemstone",
  texture: "Texture / finish",
  colour: "Colour swatch",
};
export const BADGE_COLORS = ["#F3CE5A", "#7DD3FC", "#FCA5A5"];

export const MIN_POINT_DELTA = 0.006;
export const MIN_PATH_SIZE = 0.02;
// Polygon point cap. Backend hard-rejects > 200 points per area (see
// RefineArea in models.py). We simplify on the frontend BEFORE submit
// using Douglas-Peucker, targeting `MAX_POLYGON_POINTS` so a long
// free-form lasso (often 300+ raw points on a large region) stays
// inside the limit. Buffer of 20 leaves room for the +1 close-loop
// rounding the simplifier sometimes emits.
export const MAX_POLYGON_POINTS = 180;
export const ZOOM_LEVELS = [1, 2, 4];
export const ZOOM_MIN = 1;
export const ZOOM_MAX = 8;
export const ZOOM_STEP = 0.12; // per wheel notch when using Ctrl/⌘+scroll

export function bbox(points) {
  if (!points.length) return { w: 0, h: 0 };
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  return { w: Math.max(...xs) - Math.min(...xs), h: Math.max(...ys) - Math.min(...ys) };
}

export function centroid(points) {
  if (!points.length) return { x: 0.5, y: 0.5 };
  return {
    x: points.reduce((s, p) => s + p.x, 0) / points.length,
    y: points.reduce((s, p) => s + p.y, 0) / points.length,
  };
}

export function polygonPointsAttr(points) {
  return points.map((p) => `${(p.x * 100).toFixed(2)},${(p.y * 100).toFixed(2)}`).join(" ");
}

export function clamp(v, min, max) {
  return Math.min(max, Math.max(min, v));
}

export function readAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(new Error("Failed to read file"));
    r.onload = () => resolve(r.result);
    r.readAsDataURL(file);
  });
}

// ---------------------------------------------------------------------------
// Polygon simplification — Douglas-Peucker.
// ---------------------------------------------------------------------------

/** Perpendicular distance from point `p` to the line through `a` and `b`,
 *  in normalised polygon space. */
function _perpDistance(p, a, b) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  if (dx === 0 && dy === 0) {
    const ex = p.x - a.x, ey = p.y - a.y;
    return Math.hypot(ex, ey);
  }
  // |(b-a) × (a-p)| / |b-a|
  const num = Math.abs(dy * p.x - dx * p.y + b.x * a.y - b.y * a.x);
  const den = Math.hypot(dx, dy);
  return num / den;
}

/**
 * Douglas-Peucker polygon simplification.
 *
 * Recursively drops the points that contribute the least to the polygon's
 * shape — at each step we find the point farthest from the chord between
 * the segment's endpoints; if its distance is below `epsilon` we drop
 * EVERY intermediate point (they're all closer to the chord). Result is
 * a polygon visually indistinguishable from the original at the user's
 * zoom level, but with dramatically fewer vertices.
 *
 * Designed for the Refine lasso: a free-form drag often produces 300+
 * raw points along a roughly oval boundary, while the backend caps each
 * polygon at 200. Without simplification the user sees a confusing
 * 422 "List should have at most 200 items" error and has to re-lasso.
 *
 * @param {{x:number,y:number}[]} points  In normalised [0..1] space.
 * @param {number} epsilon                Drop threshold (normalised units).
 * @returns {{x:number,y:number}[]}       Subset, in original order.
 */
export function douglasPeucker(points, epsilon) {
  if (points.length <= 2 || epsilon <= 0) return points.slice();
  const keep = new Uint8Array(points.length);
  keep[0] = 1;
  keep[points.length - 1] = 1;

  const stack = [[0, points.length - 1]];
  while (stack.length) {
    const [i0, i1] = stack.pop();
    let maxD = -1;
    let maxIdx = -1;
    for (let i = i0 + 1; i < i1; i++) {
      const d = _perpDistance(points[i], points[i0], points[i1]);
      if (d > maxD) { maxD = d; maxIdx = i; }
    }
    if (maxD > epsilon && maxIdx !== -1) {
      keep[maxIdx] = 1;
      stack.push([i0, maxIdx]);
      stack.push([maxIdx, i1]);
    }
  }
  const out = [];
  for (let i = 0; i < points.length; i++) if (keep[i]) out.push(points[i]);
  return out;
}

/**
 * Simplify a polygon down to AT MOST `targetCount` points by binary-
 * searching for the smallest Douglas-Peucker epsilon that yields a
 * point count ≤ target. Always returns ≥ 3 points (polygons need it).
 *
 * Why binary search instead of a fixed epsilon: the right epsilon
 * depends on the polygon's perimeter and curvature — a tightly curved
 * lasso around a small detail and a sweeping lasso over half the
 * canvas both produce 300+ raw points, but they need very different
 * thresholds to land at ~180. Bisecting on epsilon finds the right
 * one in ≤ 20 iterations regardless of the polygon's scale.
 */
export function simplifyPolygon(points, targetCount) {
  if (points.length <= targetCount) return points.slice();
  // Binary search between 0 and ~1 (the entire normalised canvas span).
  let lo = 0;
  let hi = 1.0;
  let best = points.slice();
  for (let iter = 0; iter < 20; iter++) {
    const mid = (lo + hi) / 2;
    const reduced = douglasPeucker(points, mid);
    if (reduced.length <= targetCount && reduced.length >= 3) {
      best = reduced;
      hi = mid; // tighten — try a smaller epsilon (more detail) next.
    } else if (reduced.length < 3) {
      hi = mid; // too aggressive — bring epsilon down.
    } else {
      lo = mid; // not aggressive enough — raise epsilon.
    }
  }
  // Defensive floor — `targetCount` of 180 paired with backend max 200
  // means we should NEVER overflow, but cap as a final safety net.
  if (best.length > targetCount) best = best.slice(0, targetCount);
  return best;
}
